import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { Card, Badge, Spinner, EmptyState } from "@/components/ui";
import { getShadow } from "@/components/ui/ShadowBadge";
import { getLevel } from "@/components/ui/XPBar";
import { useAuthStore } from "@/store/authStore";
import clsx from "clsx";

export default function MentorDashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["students"],
    queryFn: () => api.get("/students").then((r) => r.data),
    refetchInterval: 30_000,
  });

  const students = data?.students || [];

  const stats = [
    { label: "Zugewiesen",  value: students.length,           icon: "👥", color: "violet" },
    { label: "Ø XP",        value: students.length > 0 ? Math.round(students.reduce((a, s) => a + (s.xp || 0), 0) / students.length) : 0, icon: "✨", color: "purple" },
    { label: "Streak Ø",    value: students.length > 0 ? Math.round(students.reduce((a, s) => a + (s.streak || 0), 0) / students.length) : 0, icon: "🔥", color: "orange" },
  ];

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="text-xl font-black">🧑‍🤝‍🧑 Mentor Dashboard</h1>
        <p className="text-xs text-gray-400 mt-0.5">Willkommen, {user?.name?.split(" ")[0]}!</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {stats.map((s) => (
          <Card key={s.label} className="p-3 text-center">
            <span className="text-xl block mb-1">{s.icon}</span>
            <p className={`font-black text-lg text-${s.color}-400`}>{s.value}</p>
            <p className="text-[10px] text-gray-500 uppercase font-bold">{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Student list */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-sm">👥 Meine Schüler</h3>
          <span className="text-xs text-gray-400">{students.length} zugewiesen</span>
        </div>

        {isLoading ? (
          <div className="flex justify-center pt-10"><Spinner size="lg" /></div>
        ) : students.length === 0 ? (
          <EmptyState icon="👥" title="Keine Schüler" desc="Dir wurden noch keine Schüler zugewiesen." />
        ) : (
          <div className="space-y-2">
            {students.map((s) => {
              const xp     = s.xp || 0;
              const level  = getLevel(xp);
              const shadow = getShadow(xp);
              const riskFlag = s.riskFlag;
              return (
                <Card
                  key={s.id}
                  hover
                  onClick={() => navigate(`/students/${s.id}`)}
                  className={clsx("p-3.5", riskFlag && "border-red-500/30")}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xl flex-shrink-0">
                      {s.user?.avatar || "🦊"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm truncate">{s.user?.name}</p>
                        {riskFlag && <span className="text-xs">⚠️</span>}
                        <span className="text-xs">{shadow.emoji}</span>
                      </div>
                      <div className="flex gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-gray-500">Lv.{level}</span>
                        <span className="text-[10px] text-violet-400">{xp.toLocaleString("de-DE")} XP</span>
                        <span className="text-[10px] text-orange-400">🔥 {s.streak || 0}</span>
                        <span className="text-[10px] text-yellow-400">🪙 {(s.coins || 0).toLocaleString("de-DE")}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <Badge color={riskFlag ? "red" : "purple"}>
                        {riskFlag ? "⚠️ Risiko" : `Lv.${level}`}
                      </Badge>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Quick actions */}
      <section>
        <h3 className="font-black text-sm mb-3">⚡ Schnellzugriff</h3>
        <div className="grid grid-cols-2 gap-2">
          {[
            { icon: "💬", label: "Nachrichten",  sub: "Eltern & Schüler", path: "/messages", color: "from-violet-600 to-purple-700" },
            { icon: "🔑", label: "Codes",         sub: "Neue Zugänge",    path: "/codes",    color: "from-cyan-600 to-blue-700"    },
          ].map((a) => (
            <button
              key={a.path}
              onClick={() => navigate(a.path)}
              className={`bg-gradient-to-br ${a.color} rounded-2xl p-4 text-left hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 active:scale-95`}
            >
              <span className="text-2xl block mb-1">{a.icon}</span>
              <p className="font-black text-sm text-white">{a.label}</p>
              <p className="text-xs text-white/70">{a.sub}</p>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
